import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, EditIcon } from 'lucide-react';
import type { BackRef } from '@/features/smr-admin/types/backRef';
import type { SecurityGroupRecord } from '@/features/security-admin/types/security-admin.types';
import { GroupPermissionBadges } from '@/features/security-admin/components/GroupPermissionBadges';

interface Props {
  data: SecurityGroupRecord[];
  backRef?: BackRef;
  onEditPermissions: (group: SecurityGroupRecord) => void;
}

export function UserSecurityGroupDataTable({ data, backRef, onEditPermissions }: Props) {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<SecurityGroupRecord>[]>(
    () => {
      const navState = backRef ? { backRef } : undefined;

      return [
        { accessorKey: 'name', header: 'Name' },
        { accessorKey: 'dealers_count', header: 'Dealers' },
        { accessorKey: 'products_count', header: 'Products' },
        { accessorKey: 'users_count', header: 'Users' },
        {
          id: 'group_permissions',
          header: 'Group Permissions',
          enableSorting: false,
          cell: ({ row }) => (
            <GroupPermissionBadges permissions={row.original.user_permissions?.[0]?.permissions} />
          ),
        },
        {
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => (
            <div className="flex space-x-2">
              <PermissionGuard permission={{ resource: 'security_group', action: 'read' }}>
                <Tooltip content="View security group">
                  <TextLink
                    onClick={() => navigate(`/admin/security-groups/${row.original.id}`, { state: navState })}
                    color="primary"
                    ariaLabel="View security group"
                    startIcon={<EyeIcon size={20} />}
                  />
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission={{ resource: 'security_group', action: 'manage_user_permissions' }}>
                <Tooltip content="Edit permissions">
                  <TextLink
                    onClick={() => onEditPermissions(row.original)}
                    color="secondary"
                    ariaLabel={`Edit permissions for ${row.original.name}`}
                    startIcon={<EditIcon size={18} />}
                  />
                </Tooltip>
              </PermissionGuard>
            </div>
          ),
        },
      ];
    },
    [navigate, backRef, onEditPermissions],
  );

  return <Table<SecurityGroupRecord> data={data} columns={columns} enableSorting={false} />;
}
